<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { AppRouterLink, AppSwiperGallery } from '@libs/ui';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '../../../../../core/stores';
import type {
  ActivityProductCardData,
  ActivitySegmentContext,
} from '../../../models';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import AlternativeSuggestionHeader from '../AlternativeSuggestionHeader.vue';

import ActivityAlternativeProductCardContent from './ActivityAlternativeProductCardContent.vue';

interface ActivityAlternativeProductCardProps {
  productCardData: ActivityProductCardData;
  context: ActivitySegmentContext;
  date: string;
}

const props = defineProps<ActivityAlternativeProductCardProps>();

interface ActivityAlternativeProductCardEmits {
  (event: 'show-details'): void;
  (event: 'change-product'): void;
  (event: 'show-tickets'): void;
  (event: 'confirm-alternative'): void;
}

const emit = defineEmits<ActivityAlternativeProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();

// TODO: Remove extra props when we know if alternative activity is working
onMounted(() => {
  statsigEventsStore.logTripOverviewAlternativeTicketsDisplayed({
    activityCode: props.context.hotelCode,
    activityName: props.productCardData.productCardContent.header,
    date: props.date,
    destination: props.productCardData.productCardContent.subheader,
  });
});

const enableHoverColor = ref<boolean>(false);

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewAlternativeActivityGalleryImageSwipe();
}
</script>

<template>
  <div class="activity-alternative-product-card">
    <ImgProductCardLayout :enable-hover-color="enableHoverColor">
      <template #header>
        <AlternativeSuggestionHeader
          :info-text="
            $t('planner.no_availability.messages.alternative_selected_ticket')
          "
          @confirm="emit('confirm-alternative')"
        >
          <template #secondary-button>
            <AppRouterLink
              :link="
                routerStore.getActivitySelectionUrl({
                  ingredientIndex: props.context.ingredientIndex,
                })
              "
              font="font-link-s-light"
              color="color-neutral-70"
            >
              {{ $t('planner.see_other_activities') }}
            </AppRouterLink>
          </template>
        </AlternativeSuggestionHeader>
      </template>
      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="true"
          :show-number-of-slides="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'activity-alternative-product-card-swiper-gallery',
              })
          "
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          @click="emit('show-details')"
          @gallery-image-swipe="onGalleryImageSwipe"
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
        />
      </template>
      <template #content>
        <ActivityAlternativeProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          @show-details="emit('show-details')"
          @show-tickets="emit('show-tickets')"
          @mouse-enter-content="enableHoverColor = true"
          @mouse-leave-content="enableHoverColor = false"
        />
      </template>
      <template #footer>
        <ProductCardFooter
          @show-details="emit('show-details')"
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
        />
      </template>
    </ImgProductCardLayout>
  </div>
</template>
